import React, {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import {
    Archive,
    ArchiveRestore,
    Check,
    Link as LinkIcon,
    ListMusic,
    PictureInPicture2,
    Share2,
    Star,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import api from 'api/api'
import TreechatIcon from 'components/shared/icons/TreechatIcon'
import OrdinalsIcon from 'components/shared/OrdinalsIcon'
import { playTrackInDock, toggleTrackInPlaylist } from 'components/shared/MediaDock/mediaDockEvents'
import { PLAY_IN_DOCK_LABEL } from 'components/shared/MediaDock/mediaDockPlayInDock'
import {
    playlistMenuLabel,
    useMediaKeyInPlaylist,
} from 'components/shared/MediaDock/useMediaKeyInPlaylist'
import { AnswerShareTray } from 'components/answers/AnswerControls'
import useOpenRight from 'refactor/hooks/useOpenRight'
import { getAnswerExternalShareUrl, getAnswerShareUrl } from 'utils/shareUtils'
import { forgetOrdinalProvenance, useOrdinalProvenance } from './ordinalProvenance'
import { artifactMenuPosition } from './artifactMenuPolicy'
import { shouldSuppressSurfaceClick, suppressNextClick } from 'lib/suppressNextClick'
import styles from './artifact-badge.module.scss'

// The wrapper answer, narrowed to what the menu itself reads. It is passed
// through to the share tray, which reads the rest off the same object.
type ArtifactMenuAnswer = {
    id?: string
}

type ArtifactMenuItem = {
    key: string
    label: string
    icon: React.ReactNode
    onClick?: () => void | Promise<void>
    // Items that report a result in place (copy confirmations, star toggles)
    // keep the menu open so the confirmation is visible.
    keepOpen?: boolean
    active?: boolean
    disabled?: boolean
}

async function copyText(value: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(value)
        return true
    } catch {
        return false
    }
}

/**
 * Artifact actions for an ordinal wrapper card, hung off the top-right corner
 * of the hero artwork (the ordinals-stream crumb owns the top-left).
 *
 * The trigger consistently uses the Treechat mark. Everything the menu offers
 * is a read or a per-user preference — nothing in it moves value, so no item
 * routes through the upvalue pipeline.
 */
export default function ArtifactMenu({
    answer,
    origin,
    onDetails,
    onBeforeNavigate,
    onOpenStream,
    resolveAnswer,
    playlistMediaKey,
    streamOverlay = false,
    fullscreenOverlay = false,
}: {
    answer: ArtifactMenuAnswer
    origin: string
    onDetails: () => void
    onBeforeNavigate?: () => void
    /** Surfaces outside the ordinals stream pass this to get an "Ordinal stream" item. */
    onOpenStream?: () => void
    resolveAnswer?: () => Promise<ArtifactMenuAnswer | null>
    playlistMediaKey?: string | null
    streamOverlay?: boolean
    fullscreenOverlay?: boolean
}) {
    // Provenance only feeds the open menu's items. A market page renders one menu
    // per card, so fetching on mount cost one request per card; fetch once the
    // pointer or focus reaches the trigger instead.
    const [provenanceWanted, setProvenanceWanted] = useState(false)
    const wantProvenance = useCallback(() => setProvenanceWanted(true), [])
    const provenance = useOrdinalProvenance(provenanceWanted ? origin : null)
    const { openRight } = useOpenRight()
    const queryClient = useQueryClient()
    const menuId = useId()
    const [open, setOpen] = useState(false)
    const [shareOpen, setShareOpen] = useState(false)
    const [flash, setFlash] = useState<string | null>(null)
    const [starred, setStarred] = useState(false)
    const inPlaylist = useMediaKeyInPlaylist(playlistMediaKey)
    const [starPending, setStarPending] = useState(false)
    const [archived, setArchived] = useState(false)
    const [archivePending, setArchivePending] = useState(false)
    const [resolvedAnswer, setResolvedAnswer] = useState<ArtifactMenuAnswer | null>(null)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const menuListRef = useRef<HTMLDivElement | null>(null)
    const flashTimeoutRef = useRef<number | null>(null)
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)

    useEffect(() => {
        setStarred(!!provenance?.starred)
    }, [provenance?.starred])

    useEffect(() => {
        setArchived(!!provenance?.archived)
    }, [provenance?.archived])

    useEffect(
        () => () => {
            if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current)
        },
        [],
    )

    const flashLabel = useCallback((key: string) => {
        setFlash(key)
        if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current)
        flashTimeoutRef.current = window.setTimeout(() => setFlash(null), 1600)
    }, [])

    const updateMenuPosition = useCallback(() => {
        const rect = buttonRef.current?.getBoundingClientRect()
        if (!rect) return
        const menuRect = menuListRef.current?.getBoundingClientRect()
        const next = artifactMenuPosition({
            triggerTop: rect.top,
            triggerBottom: rect.bottom,
            triggerRight: rect.right,
            menuWidth: menuRect?.width || 190,
            menuHeight: menuRect?.height || 0,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
        })
        setMenuPosition(previous =>
            previous && previous.top === next.top && previous.left === next.left ? previous : next,
        )
    }, [])

    useEffect(() => {
        if (!open) return
        updateMenuPosition()

        const handlePointerDown = (event: PointerEvent) => {
            const node = event.target as Node
            if (menuListRef.current?.contains(node) || buttonRef.current?.contains(node)) return
            // Dismissal only: the click this pointerdown produces must not
            // open the tile or the market card under the pointer.
            suppressNextClick(event, click => shouldSuppressSurfaceClick(click.target))
            setOpen(false)
        }
        const handleFocusIn = (event: FocusEvent) => {
            const node = event.target as Node
            if (menuListRef.current?.contains(node) || buttonRef.current?.contains(node)) return
            setOpen(false)
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            event.preventDefault()
            setOpen(false)
            buttonRef.current?.focus()
        }
        const handleViewportChange = () => updateMenuPosition()

        document.addEventListener('pointerdown', handlePointerDown, true)
        document.addEventListener('focusin', handleFocusIn, true)
        document.addEventListener('keydown', handleKeyDown)
        window.addEventListener('resize', handleViewportChange)
        window.addEventListener('scroll', handleViewportChange, true)
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true)
            document.removeEventListener('focusin', handleFocusIn, true)
            document.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('resize', handleViewportChange)
            window.removeEventListener('scroll', handleViewportChange, true)
        }
    }, [open, updateMenuPosition])

    const mintingQuestId = provenance?.minting_quest_id || null
    const mintingAnswerId = provenance?.minting_answer_id || null
    const bsvOrdinalId = provenance?.bsv_ordinal_id || null
    const menuAnswer = resolvedAnswer || answer
    const shareUrl = useMemo(
        () => (menuAnswer?.id ? getAnswerExternalShareUrl(menuAnswer) : null),
        [menuAnswer?.id],
    )

    const resolveShareAnswer = useCallback(async () => {
        if (shareUrl) return menuAnswer
        const nextAnswer = await resolveAnswer?.()
        if (!nextAnswer?.id) return null
        setResolvedAnswer(nextAnswer)
        return nextAnswer
    }, [menuAnswer, resolveAnswer, shareUrl])

    const openMintingThread = useCallback(() => {
        if (!mintingQuestId) return
        onBeforeNavigate?.()
        openRight({ questId: mintingQuestId, answerId: mintingAnswerId || undefined })
    }, [mintingAnswerId, mintingQuestId, onBeforeNavigate, openRight])

    const invalidateOrdinalState = useCallback(() => {
        forgetOrdinalProvenance(origin)
        void queryClient.invalidateQueries({
            predicate: query => {
                const root = Array.isArray(query.queryKey) ? query.queryKey[0] : null
                return root === 'treechatOrdinals' || root === 'ordinalsMediaStream'
            },
        })
    }, [origin, queryClient])

    const toggleStar = useCallback(async () => {
        if (!bsvOrdinalId || starPending) return
        const previous = starred
        setStarred(!previous)
        setStarPending(true)
        try {
            const res = await api.toggleOrdinalShowInProfile(bsvOrdinalId)
            setStarred(!!res?.users_ordinal?.show_in_profile)
            invalidateOrdinalState()
        } catch {
            setStarred(previous)
            flashLabel('star-error')
        } finally {
            setStarPending(false)
        }
    }, [bsvOrdinalId, flashLabel, invalidateOrdinalState, starPending, starred])

    const toggleArchive = useCallback(async () => {
        if (!bsvOrdinalId || archivePending) return
        const previous = archived
        setArchived(!previous)
        setArchivePending(true)
        try {
            const res = await api.toggleOrdinalHidden(bsvOrdinalId)
            setArchived(!!res?.users_ordinal?.hidden)
            invalidateOrdinalState()
        } catch {
            setArchived(previous)
            flashLabel('archive-error')
        } finally {
            setArchivePending(false)
        }
    }, [archivePending, archived, bsvOrdinalId, flashLabel, invalidateOrdinalState])

    const items = useMemo<ArtifactMenuItem[]>(() => {
        const entries: ArtifactMenuItem[] = []

        if (shareUrl || resolveAnswer) {
            entries.push({
                key: 'link',
                label: flash === 'link' ? 'Copied link' : 'Link',
                icon: <LinkIcon size={14} />,
                keepOpen: true,
                onClick: async () => {
                    const shareAnswer = await resolveShareAnswer()
                    const resolvedShareUrl = shareAnswer?.id ? getAnswerShareUrl(shareAnswer) : null
                    if (resolvedShareUrl && (await copyText(resolvedShareUrl))) {
                        flashLabel('link')
                    }
                },
            })
            entries.push({
                key: 'share',
                label: 'Share',
                icon: <Share2 size={14} />,
                onClick: async () => {
                    if (await resolveShareAnswer()) setShareOpen(true)
                },
            })
        }

        if (mintingQuestId) {
            entries.push({
                key: 'minting-thread',
                label: 'Minting Thread',
                icon: <TreechatIcon size={16} className={styles.menuTreechatIcon} />,
                onClick: openMintingThread,
            })
        }

        if (playlistMediaKey) {
            entries.push({
                key: 'playlist',
                label: playlistMenuLabel(inPlaylist),
                icon: inPlaylist ? <Check size={14} /> : <ListMusic size={14} />,
                onClick: () => {
                    toggleTrackInPlaylist(playlistMediaKey, 'ordinal_media_menu')
                },
            })
            entries.push({
                key: 'play-in-dock',
                label: PLAY_IN_DOCK_LABEL,
                icon: <PictureInPicture2 size={14} />,
                onClick: () => {
                    playTrackInDock(playlistMediaKey, 'ordinal_media_menu')
                },
            })
        }

        if (onOpenStream) {
            entries.push({
                key: 'stream',
                label: 'Ordinal stream',
                icon: <OrdinalsIcon size={14} />,
                onClick: () => {
                    onBeforeNavigate?.()
                    onOpenStream()
                },
            })
        }

        entries.push({
            key: 'details',
            label: 'Details',
            icon: <OrdinalsIcon size={14} />,
            onClick: onDetails,
        })

        // Owner-only. Starring is not decoration: an owner-featured artifact is
        // one of the vouches that admits an inscription into the public stream.
        if (provenance?.viewer_is_owner && bsvOrdinalId) {
            entries.push({
                key: 'star',
                label:
                    flash === 'star-error'
                        ? 'Could not update star'
                        : starPending
                        ? starred
                            ? 'Starring…'
                            : 'Unstarring…'
                        : starred
                        ? 'Starred'
                        : 'Star',
                icon: <Star size={14} fill={starred ? 'currentColor' : 'none'} />,
                keepOpen: true,
                active: starred,
                disabled: starPending,
                onClick: toggleStar,
            })
            entries.push({
                key: 'archive',
                label:
                    flash === 'archive-error'
                        ? 'Could not update archive'
                        : archivePending
                        ? archived
                            ? 'Archiving…'
                            : 'Unarchiving…'
                        : archived
                        ? 'Unarchive'
                        : 'Archive',
                icon: archived ? <ArchiveRestore size={14} /> : <Archive size={14} />,
                keepOpen: true,
                active: archived,
                disabled: archivePending,
                onClick: toggleArchive,
            })
        }

        return entries
    }, [
        archivePending,
        archived,
        bsvOrdinalId,
        flash,
        flashLabel,
        mintingQuestId,
        onBeforeNavigate,
        onDetails,
        onOpenStream,
        openMintingThread,
        origin,
        inPlaylist,
        playlistMediaKey,
        provenance?.viewer_is_owner,
        resolveAnswer,
        resolveShareAnswer,
        shareUrl,
        starPending,
        starred,
        toggleStar,
        toggleArchive,
    ])

    useLayoutEffect(() => {
        if (!open || !menuPosition) return
        updateMenuPosition()
    }, [items, menuPosition, open, updateMenuPosition])

    useLayoutEffect(() => {
        if (!open || !menuPosition) return
        const firstItem = menuListRef.current?.querySelector<HTMLElement>(
            '[role="menuitem"]:not(:disabled)',
        )
        firstItem?.focus()
    }, [open])

    const handleMenuKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
        const menuItems = Array.from(
            menuListRef.current?.querySelectorAll<HTMLElement>(
                '[role="menuitem"]:not(:disabled)',
            ) || [],
        )
        if (!menuItems.length) return

        event.preventDefault()
        const currentIndex = menuItems.indexOf(document.activeElement as HTMLElement)
        const nextIndex =
            event.key === 'Home'
                ? 0
                : event.key === 'End'
                ? menuItems.length - 1
                : event.key === 'ArrowUp'
                ? (currentIndex <= 0 ? menuItems.length : currentIndex) - 1
                : (currentIndex + 1) % menuItems.length
        menuItems[nextIndex]?.focus()
    }, [])

    const TriggerIcon = (
        <TreechatIcon
            size={streamOverlay || fullscreenOverlay ? 24 : 18}
            className={styles.menuTreechatIcon}
        />
    )

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={cn(
                    styles.artifactMenuButton,
                    streamOverlay && styles.artifactMenuButtonStream,
                    fullscreenOverlay && styles.artifactMenuButtonFullscreen,
                )}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-label="Ordinal actions"
                title="Ordinal actions"
                onPointerEnter={wantProvenance}
                onPointerDown={wantProvenance}
                onFocus={wantProvenance}
                onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    wantProvenance()
                    updateMenuPosition()
                    setOpen(prev => !prev)
                }}
            >
                {TriggerIcon}
            </button>

            {open &&
                menuPosition &&
                createPortal(
                    <div
                        id={menuId}
                        ref={menuListRef}
                        className={cn(
                            styles.artifactMenuList,
                            fullscreenOverlay && styles.artifactMenuListFullscreen,
                        )}
                        style={{ top: menuPosition.top, left: menuPosition.left }}
                        role="menu"
                        aria-label="Ordinal actions"
                        onClick={event => event.stopPropagation()}
                        onKeyDown={handleMenuKeyDown}
                    >
                        {items.map(item => {
                            const content = (
                                <>
                                    <span className={styles.artifactMenuItemIcon}>{item.icon}</span>
                                    {item.label}
                                </>
                            )

                            return (
                                <button
                                    key={item.key}
                                    type="button"
                                    className={cn(
                                        styles.artifactMenuItem,
                                        item.active && styles.artifactMenuItemActive,
                                    )}
                                    role="menuitem"
                                    disabled={item.disabled}
                                    aria-busy={item.disabled || undefined}
                                    onClick={event => {
                                        event.preventDefault()
                                        event.stopPropagation()
                                        if (!item.keepOpen) setOpen(false)
                                        void item.onClick?.()
                                    }}
                                >
                                    {content}
                                </button>
                            )
                        })}
                    </div>,
                    document.body,
                )}

            <AnswerShareTray
                answer={menuAnswer}
                copyContent={null}
                isOpen={shareOpen}
                onDismiss={() => setShareOpen(false)}
                overlayClassName={
                    fullscreenOverlay ? styles.artifactMenuShareTrayOverlay : undefined
                }
                shareUrl={shareUrl}
            />
        </>
    )
}
